import type { UrgentCareOrderOffer } from '@/lib/store/api/urgentCareApi'

export type UrgentCareOwnerPaymentType = 'insurance' | 'cash' | 'mdsave' | string

export type UrgentCareOwnerOption = {
    ownerId: string
    ownerName: string
    fulfillmentOfferId: string
    paymentType: UrgentCareOwnerPaymentType | null
    priceCents: number | null
    /** Square partner mark URL when the offers payload includes one. */
    logoUrl: string | null
    /** True when the owner supports in-app scheduling (or the flag is omitted). */
    integrated: boolean
    bookingUrl: string | null
}

type OfferPrice = {
    cents: number | null
    billing: 'insurance' | 'cash'
}

function billingFor(offer: UrgentCareOrderOffer): OfferPrice['billing'] {
    return offer.type === 'insurance' ? 'insurance' : 'cash'
}

function priceFor(offer: UrgentCareOrderOffer): OfferPrice {
    const amount = offer.price?.amount
    return {
        cents: typeof amount === 'number' ? amount : null,
        billing: billingFor(offer),
    }
}

/** Lower cents wins; insurance wins a price tie. */
function isPreferredPrice(candidate: OfferPrice, current: OfferPrice): boolean {
    if (candidate.cents == null) return false
    if (current.cents == null) return true
    if (candidate.cents !== current.cents) return candidate.cents < current.cents
    return candidate.billing === 'insurance' && current.billing !== 'insurance'
}

function ownerNameFor(offer: UrgentCareOrderOffer): string {
    const displayName = offer.fulfillment_owner?.display_name?.trim()
    if (displayName) return displayName
    const name = offer.fulfillment_owner?.name?.trim()
    if (name) return name
    return 'Urgent care partner'
}

/**
 * Collapse FO rows to one preferred offer per fulfillment owner (cheapest;
 * insurance wins a tie). Skips offers missing an FO id or owner id.
 */
export function toUrgentCareOwnerOptions(offers: UrgentCareOrderOffer[]): UrgentCareOwnerOption[] {
    const byOwner = new Map<string, UrgentCareOrderOffer>()

    for (const offer of offers) {
        const rawOwnerId = offer.fulfillment_owner?.id
        const ownerId = rawOwnerId == null ? '' : String(rawOwnerId).trim()
        const fulfillmentOfferId = offer.fulfillment_offer_id?.trim()
        if (!ownerId || !fulfillmentOfferId) continue

        const existing = byOwner.get(ownerId)
        if (!existing || isPreferredPrice(priceFor(offer), priceFor(existing))) {
            byOwner.set(ownerId, offer)
        }
    }

    return Array.from(byOwner.entries(), ([ownerId, offer]) => {
        const amount = offer.price?.amount
        const logoUrl = offer.fulfillment_owner?.logo_url?.trim() || null
        return {
            ownerId,
            ownerName: ownerNameFor(offer),
            fulfillmentOfferId: offer.fulfillment_offer_id,
            paymentType: offer.type ?? null,
            priceCents: typeof amount === 'number' ? amount : null,
            logoUrl,
            integrated: offer.fulfillment_owner?.direct_scheduling_integrated !== false,
            bookingUrl: offer.booking_url?.trim() || null,
        }
    })
}

/** External-only partners open booking_url; everything else can PATCH the order. */
export function isExternalOnlyOwnerOption(option: UrgentCareOwnerOption): boolean {
    return !option.integrated && Boolean(option.bookingUrl)
}

/**
 * When offers collapse to a single in-app owner, return that FO id for auto-select.
 * External-only partners are left for the chooser / booking_url path.
 */
export function getAutoSelectFulfillmentOfferId(offers: UrgentCareOrderOffer[]): string | undefined {
    const ownerOptions = toUrgentCareOwnerOptions(offers)
    if (ownerOptions.length !== 1) return undefined

    const option = ownerOptions[0]
    if (!option || isExternalOnlyOwnerOption(option)) return undefined

    const fulfillmentOfferId = option.fulfillmentOfferId?.trim()
    return fulfillmentOfferId || undefined
}
